import { authorizationManagerService, axiosService } from '..';
import * as moment from 'moment';
import i18n from '@/plugins/i18n';

export default class QueryCampaignsService {
    public async execute(): Promise<QueryCampaignsOutputDto[]> {
        let additionalFilter: string = '';
        if (authorizationManagerService.isOwnerOfAnyCampaign) {
            additionalFilter += ' and (';
            for (const id of authorizationManagerService.isOwnerOfCampaignIds) {
                additionalFilter += `id eq ${id} or `;
            }
            additionalFilter = `${additionalFilter.substring(0,additionalFilter.length - 4)})`;
        }

        const response: any = await axiosService.instance.get('campaigns/organization/query', {
            params: {
                $filter: `isOutOfDate eq false${additionalFilter}`,
                $orderby: 'displayName asc'
            }
        });

        const result: QueryCampaignsOutputDto[] = [];

        response.data.items.forEach((item: any) => {
            result.push(
                new QueryCampaignsOutputDto(
                    item.id,
                    item.internalName,
                    item.displayName,
                    item.isOutOfDate,
                    item.startDateUtc,
                    item.purchaseEndDateUtc,
                    item.redemptionEndDateUtc,
                    item.justificationEndDateUtc,
                    item.endDateUtc,
                    item.isPurchaseProcessEnded,
                    item.isRedemptionProcessEnded,
                    item.isJustificationProcessEnded,
                    moment.utc() > moment.utc(item.endDateUtc),
                    new QueryCampaignsConfigurationOutputDto(item.configuration.type, item.configuration.businessIdThatOffersAllVouchers, item.configuration.listVouchersGroupedByOrderInfo),
                    item.enableValidation,
                    item.maxCountVouchersByPurchase
                )
            );
        });

        return result;
    }
}

export class QueryCampaignsOutputDto {
    public readonly id: number = 0;
    public readonly internalName: string = '';
    public readonly displayName: string = '';
    public readonly isOutOfDate: boolean = true;
    public readonly startDateUtc?: moment.Moment;
    public readonly startDate?: string;
    public readonly purchaseEndDateUtc?: moment.Moment;
    public readonly purchaseEndDate?: string = '';
    public readonly redemptionEndDateUtc?: moment.Moment;
    public readonly redemptionEndDate?: string = '';
    public readonly justificationEndDateUtc?: moment.Moment;
    public readonly justificationEndDate?: string = '';
    public readonly endDateUtc?: moment.Moment;
    public readonly endDate?: string = '';
    public readonly isPurchaseProcessEnded: boolean = true;
    public readonly isRedemptionProcessEnded: boolean = true;
    public readonly isJustificationProcessEnded: boolean = true;
    public readonly isClosed: boolean = true;
    public readonly configuration: QueryCampaignsConfigurationOutputDto | null = null;
    public readonly enableValidation: boolean = false;
    public readonly maxCountVouchersByPurchase: number | null = null;

    constructor(
        id: number,
        internalName: string,
        displayName: string,
        isOutOfDate: boolean,
        startDateUtc: moment.Moment,
        purchaseEndDateUtc: moment.Moment,
        redemptionEndDateUtc: moment.Moment,
        justificationEndDateUtc: moment.Moment,
        endDateUtc: moment.Moment,
        isPurchaseProcessEnded: boolean,
        isRedemptionProcessEnded: boolean,
        isJustificationProcessEnded: boolean,
        isClosed: boolean,
        configuration: QueryCampaignsConfigurationOutputDto,
        enableValidation: boolean,
        maxCountVouchersByPurchase: number | null
    ) {
        this.id = id;
        this.internalName = internalName;
        this.displayName = displayName;
        this.isOutOfDate = isOutOfDate;
        this.startDateUtc = startDateUtc;
        this.startDate = this.startDateUtc ? moment.utc(this.startDateUtc).locale(i18n.locale).format('LL') : undefined;
        this.purchaseEndDateUtc = purchaseEndDateUtc;
        this.purchaseEndDate = this.purchaseEndDateUtc ? moment.utc(this.purchaseEndDateUtc).locale(i18n.locale).format('LL') : undefined;
        this.redemptionEndDateUtc = redemptionEndDateUtc;
        this.redemptionEndDate = this.redemptionEndDateUtc ? moment.utc(this.redemptionEndDateUtc).locale(i18n.locale).format('LL') : undefined;
        this.justificationEndDateUtc = justificationEndDateUtc;
        this.justificationEndDate = this.justificationEndDateUtc ? moment.utc(this.justificationEndDateUtc).locale(i18n.locale).format('LL') : undefined;
        this.endDateUtc = endDateUtc;
        this.endDate = this.endDateUtc ? moment.utc(this.endDateUtc).locale(i18n.locale).format('LL') : undefined;
        this.isPurchaseProcessEnded = isPurchaseProcessEnded;
        this.isRedemptionProcessEnded = isRedemptionProcessEnded;
        this.isJustificationProcessEnded = isJustificationProcessEnded;
        this.isClosed = isClosed;
        this.configuration = configuration;
        this.enableValidation = enableValidation;
        this.maxCountVouchersByPurchase = maxCountVouchersByPurchase;
    }
}

export class QueryCampaignsConfigurationOutputDto {
    public readonly type: number = 0;
    public readonly businessIdThatOffersAllVouchers: number | null = null;
    public readonly listVouchersGroupedByOrderInfo: boolean = false;

    constructor(type: number, businessIdThatOffersAllVouchers: number, listVouchersGroupedByOrderInfo: boolean) {
        this.type = type;
        this.businessIdThatOffersAllVouchers = businessIdThatOffersAllVouchers;
        this.listVouchersGroupedByOrderInfo = listVouchersGroupedByOrderInfo;
    }
}

export const queryCampaignsService = new QueryCampaignsService();
